/**
 * WebLeftMenu — the web app's navigation, composed into the shared
 * `Sidebar` primitive's children slot.
 *
 * Now gated + wired to the router (2026-06-02 routing spec; phase-ladder
 * rewrite 2026-08-03):
 *   - Items/sections are filtered by `useFeatureAccess`: a normal user
 *     sees ONLY released features; `?preview=1`/`2`/`3` reveal each
 *     phase's off items as navigable rows with a `coming soon` badge
 *     (Administration: `nice to have`) ONLY while the active mode still
 *     phase-matches the item (`shouldShowComingSoonBalloon`); `?preview=all`
 *     reveals every phase + radar-only item, always balloon'd. Empty
 *     sections (Screening/Assessing — no pages) appear only in preview as
 *     inert headers.
 *   - The active item is derived from the URL (`useLocation`), not a
 *     prop — the LONGEST matching item `path` wins so nested routes (e.g.
 *     `/research/imports/:id`) keep the parent item active, and exactly
 *     ONE row is `aria-current="page"` even when one item path is a prefix
 *     of another (e.g. `/operations/searches` vs `/operations/searches/detail`).
 *   - `onSelect(path)` is called with the item's router `path`; the shell
 *     wires it to `navigate`.
 *
 * Tokenized: only token utilities + Tailwind's 4px scale + `text-*`
 * roles — no inline styles, no arbitrary `text-[Npx]`.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { NavStatusBadge, NewBadge, UpdatedBadge } from "@nu-assist/ui";
import {
  pageRevealedByMode,
  shouldShowComingSoonBalloon,
  useFeatureAccess,
} from "../features/useFeatureAccess";
import {
  LEFT_MENU_SECTIONS,
  type LeftMenuItem,
  type LeftMenuSection,
  announcementFor,
} from "./leftMenuModel";
import type { PreviewMode } from "../features/PreviewProvider";

export interface WebLeftMenuProps {
  /** Selection handler — called with the item's router path. */
  onSelect?: (path: string) => void;
}

function NavRow({
  item,
  active,
  previewMode,
  released,
  onSelect,
}: {
  item: LeftMenuItem;
  active: boolean;
  previewMode: PreviewMode;
  /**
   * Whether the page itself is RELEASED (i.e. the flag is on — would be
   * visible without any preview). Drives whether to show the preview
   * badge: a released item that happens to be visible during preview
   * mode is still a real, navigable page and must NOT carry a
   * "coming soon" badge. Only items revealed solely by the active
   * preview mode (released=false) get the balloon, and only when the
   * active mode still phase-matches the item (see
   * `shouldShowComingSoonBalloon`).
   *
   * Pre-2026-06-12 bug: the badge was tied only to `previewing`, so e.g.
   * `closing-email-template` (flag=true, released) was tagged "coming
   * soon" under ?preview=1 even though it renders the real
   * EmailTemplateEditor page when navigated to. Now matches the
   * Dashboard HomeCard treatment (`access.previewing && !released`).
   */
  released: boolean;
  onSelect?: (path: string) => void;
}) {
  const Icon = item.icon;
  const base =
    "w-full text-left flex items-center gap-2.5 py-2 text-body bg-transparent border-0 outline-none transition-colors rounded-none cursor-pointer focus-visible:ring-2 focus-visible:ring-accent-muted";
  // Child-item indent — the row's icon column lines up with the section
  // heading's NUMBER column, not its icon column. The section header is
  // `px-4` (16px) + an 18px icon + `gap-2` (8px), so the section number
  // text starts at 42px from the container edge. `pl-[42px]` lands the
  // row's icon under that number column, communicating "child of the
  // section above" at a glance. All child items share this single indent
  // level — the v37 extra-nested sub-row (used for "Closing Email
  // Template Editor" + "Searches Import") was retired on 2026-06-16
  // because the 2-level visual model didn't scale to N-level future
  // sections.
  const padding = "pl-[42px] pr-4";
  const tone = active
    ? "text-accent"
    : item.muted
      ? "text-text-muted"
      : "text-text-secondary hover:text-text";

  // The self-expiring pill shows by DEFAULT for a RELEASED item, and — since
  // 2026-09-02 (production-freeze review flow) — also for an item revealed by
  // the active preview mode while its announcement window is open.
  // `announcementFor` owns the whole rule (released-or-revealed gate, window
  // gate, new-over-updated precedence), so an item carrying both fields
  // renders one pill rather than two stacked ones (`ml-auto` would push the
  // second into the first and the row would read as noise).
  const revealedByMode = pageRevealedByMode(previewMode, item.id);
  const badge = announcementFor(item.id, { revealed: revealedByMode });
  const showNewBadge = badge === "new";
  const showUpdatedBadge = badge === "updated";

  // The preview badge appears ONLY for items revealed BY the preview
  // mode (not released-and-real) AND whose own phase still matches the
  // active mode (`shouldShowComingSoonBalloon` — the phase-match
  // suppression rule: once the active mode moves past a phase, that
  // phase's items lose the balloon even though they stay visible).
  // `?preview=all` never suppresses (flat radar, always balloon'd). A
  // released item that's incidentally visible during preview is a real
  // navigable page; tagging it "coming soon" would lie about its status
  // (closing-email-template). Mirrors the Dashboard HomeCard's
  // `access.previewing && !released` rule for parity, gated first on
  // `!released` (unchanged rule: a released item never shows the
  // balloon).
  //
  // `badge === null` is the 2026-09-02 tiebreak between the two badges: a
  // revealed item with an ACTIVE pill shows the pill, not the balloon (one
  // badge per row, and the pill is the more specific claim). When the
  // window lapses, `announcementFor` returns null and the balloon resumes.
  const showPreviewBadge =
    !released &&
    badge === null &&
    shouldShowComingSoonBalloon(previewMode, item.id);

  // External-link shortcut (e.g. AgendaGPT n8n form): render an anchor that
  // opens in a new tab instead of the in-app navigate() button. A trailing
  // ExternalLink glyph signals the off-app jump. Same token styling as a row.
  if (item.externalUrl) {
    return (
      <a
        href={item.externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`nav-item-${item.id}`}
        className={`${base} ${padding} ${tone} no-underline`}
      >
        <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">{item.navLabel ?? item.label}</span>
        <span className="sr-only"> (opens in a new tab)</span>
        <ExternalLink className="w-3.5 h-3.5 ml-auto shrink-0" aria-hidden="true" />
      </a>
    );
  }

  return (
    <button
      type="button"
      data-testid={`nav-item-${item.id}`}
      aria-current={active ? "page" : undefined}
      onClick={() => onSelect?.(item.path)}
      className={`${base} ${padding} ${tone}`}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
      <span className="truncate">{item.navLabel ?? item.label}</span>
      {/* Coming-soon (or nice-to-have) badge — ONLY for off items that are
          revealed solely by the preview tier (not for real released pages
          incidentally visible during preview). */}
      {showPreviewBadge ? (
        <NavStatusBadge
          kind={item.previewBadge ?? "coming-soon"}
          className="ml-auto"
        />
      ) : null}
      {/* Self-expiring "new" pill — released items (shown by default, not
          preview-gated) and preview-revealed items while the window is
          open. Bare "new" (no date) to keep the sidebar row compact. */}
      {showNewBadge ? <NewBadge className="ml-auto" /> : null}
      {/* Self-expiring "updated" pill — released items that replaced an
          earlier surface (Baby Chops v2). Bare "updated" (no date) for the
          same reason the new pill is bare: the sidebar row is narrow. */}
      {showUpdatedBadge ? <UpdatedBadge className="ml-auto" /> : null}
    </button>
  );
}

function SectionHeader({
  section,
  open,
  collapsible,
  previewing,
  onToggle,
}: {
  section: LeftMenuSection;
  open: boolean;
  collapsible: boolean;
  previewing: boolean;
  onToggle?: () => void;
}) {
  const Icon = section.icon;
  const base =
    "w-full text-left flex items-center gap-2 px-4 pt-4 pb-1.5 text-h3 font-bold uppercase tracking-normal text-text-secondary";
  const label = (
    <>
      {/* 18px = the section icon (was 16px / w-4) bumped +2px, matching the
          item icons' +2px (w-3 → w-3.5) and the text's +2px. No Tailwind
          token lands on 18px, so an arbitrary value is used (cf.
          DockablePanel's w-[30px]). */}
      <Icon className="w-[18px] h-[18px] shrink-0" aria-hidden="true" />
      <span>{section.navLabel ?? section.label}</span>
    </>
  );

  if (collapsible) {
    return (
      <button
        type="button"
        data-testid={`nav-section-${section.id}`}
        aria-expanded={open}
        onClick={onToggle}
        className={`${base} bg-transparent border-0 outline-none cursor-pointer transition-colors hover:text-text focus-visible:ring-2 focus-visible:ring-accent-muted`}
      >
        {label}
        {open ? (
          <ChevronDown className="w-3.5 h-3.5 ml-auto shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 ml-auto shrink-0" aria-hidden="true" />
        )}
      </button>
    );
  }

  // Non-collapsible (empty) section — only shown in preview; mark it
  // coming-soon so the roadmap is legible there.
  return (
    <div data-testid={`nav-section-${section.id}`} className={base}>
      {label}
      {previewing ? (
        <NavStatusBadge kind="coming-soon" className="ml-auto" />
      ) : null}
    </div>
  );
}

function Section({
  section,
  activeItemPath,
  onSelect,
}: {
  section: LeftMenuSection;
  /** The single resolved active item path (longest match), or null. */
  activeItemPath: string | null;
  onSelect?: (path: string) => void;
}) {
  const access = useFeatureAccess();
  const previewing = access.previewing;
  const previewMode = access.previewMode;

  // Snapshot each visible item with its `released` flag so the row can
  // decide whether to show the preview "coming soon" badge. A released
  // item that's incidentally visible during preview is a real navigable
  // page and must render WITHOUT a badge — only items revealed solely by
  // the preview mode (released=false) AND still phase-matching the
  // active mode get the badge.
  const visibleItems = section.items
    .map((item) => ({ item, access: access.page(item.sectionId, item.id) }))
    .filter(({ access: a }) => a.visible);
  const holdsActive = visibleItems.some(
    ({ item }) => item.path === activeItemPath,
  );
  // All hooks must run before any conditional return (rules-of-hooks).
  // Seed open-state once; the effect below keeps it open whenever the
  // section holds the active route (it re-opens on navigation — fix I2).
  const [open, setOpen] = useState(
    (section.defaultOpen ?? false) || holdsActive,
  );

  // I2 — collapsed section must not hide the active item. The sidebar
  // stays mounted across navigations, so a section that comes to hold the
  // active route AFTER mount (deep-link / internal nav into a non-default-
  // open section) must auto-open. Recompute on every `holdsActive` change.
  useEffect(() => {
    if (holdsActive) setOpen(true);
  }, [holdsActive]);

  // Section visible iff the resolver says so (anti-empty-shell aware:
  // released only when it has ≥1 visible page; preview reveals all).
  if (!access.section(section.id).visible) return null;

  // Empty sections (no pages) appear ONLY in preview as inert headers —
  // they have no children to toggle, so render non-collapsible.
  const isEmpty = section.items.length === 0;
  const collapsible = section.collapsible && !isEmpty;
  const showItems = collapsible ? open : true;

  return (
    <div className="mb-1">
      <SectionHeader
        section={section}
        open={open}
        collapsible={collapsible}
        previewing={previewing}
        onToggle={collapsible ? () => setOpen((prev) => !prev) : undefined}
      />
      {showItems
        ? visibleItems.map(({ item, access: itemAccess }) => (
            <NavRow
              key={item.id}
              item={item}
              active={item.path === activeItemPath}
              previewMode={previewMode}
              released={itemAccess.released}
              onSelect={onSelect}
            />
          ))
        : null}
    </div>
  );
}

/**
 * Active-from-URL (longest-match): an item matches the current path when
 * the path equals its `path` or is nested beneath it (so
 * `/research/imports/123` matches the Imports item). When several item
 * paths match (one is a prefix of another, e.g. `/operations/searches` vs
 * `/operations/searches/detail`), the LONGEST matching path wins — so
 * exactly one item is active.
 */
export function resolveActiveItemPath(
  currentPath: string,
  itemPaths: readonly string[],
): string | null {
  let best: string | null = null;
  for (const itemPath of itemPaths) {
    const matches =
      currentPath === itemPath || currentPath.startsWith(`${itemPath}/`);
    if (matches && (best === null || itemPath.length > best.length)) {
      best = itemPath;
    }
  }
  return best;
}

export function WebLeftMenu({ onSelect }: WebLeftMenuProps) {
  const { pathname } = useLocation();
  // Resolve the single active item across ALL sections' items, so a prefix
  // ancestor never lights up alongside the deeper, more specific match.
  const allItemPaths = LEFT_MENU_SECTIONS.flatMap((s) =>
    s.items.map((i) => i.path),
  );
  const activeItemPath = resolveActiveItemPath(pathname, allItemPaths);
  return (
    <>
      {LEFT_MENU_SECTIONS.map((section) => (
        <Section
          key={section.id}
          section={section}
          activeItemPath={activeItemPath}
          onSelect={onSelect}
        />
      ))}
    </>
  );
}
